import { useState, useRef } from 'react';
import { Upload, Loader2, Image as ImageIcon, User, Globe, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../context/AuthContext';
import { API, API_BASE } from '../../config/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

export function PublishingPanel({
  publishingBanner, setPublishingBanner,
  artistProfile,
  advancedAnalytics,
  profileFormData, setProfileFormData,
  onProfileSaved,
}) {
  const pubBannerRef = useRef(null);
  const [uploadingPubBanner, setUploadingPubBanner] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPubBanner(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imageUrl = `${API_BASE}${data.url}`;
      await api.put(`${API}/site-settings`, { publishing_banner: imageUrl });
      setPublishingBanner(imageUrl);
      toast.success('Banner updated!');
    } catch (err) {
      toast.error('Failed to upload banner');
    } finally {
      setUploadingPubBanner(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`${API}/artist-profile`, profileFormData, { withCredentials: true });
      toast.success('Profile saved!');
      setProfileDialogOpen(false);
      onProfileSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save profile');
    }
  };

  return (
    <div className="space-y-6" data-testid="publishing-content">
      {/* Publishing Banner */}
      <div className="relative w-full h-48 bg-[#141414] border border-[#262626] overflow-hidden group">
        {publishingBanner ? (
          <img src={publishingBanner} alt="Publishing Banner" className="w-full h-full object-cover" data-testid="publishing-banner-img" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#262626]">
            <ImageIcon className="w-16 h-16" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <input type="file" accept="image/*" ref={pubBannerRef} onChange={handleBannerUpload} className="hidden" />
          <Button onClick={() => pubBannerRef.current?.click()} disabled={uploadingPubBanner}
            className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="upload-pub-banner-btn">
            {uploadingPubBanner ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> {publishingBanner ? 'Change Banner' : 'Upload Banner'}</>}
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-[#737373] -mt-4" data-testid="pub-banner-dimensions-hint">
        <span className="text-[#DAA520] font-bold">Recommended:</span> 1920×480px (4:1 aspect ratio) · JPG / PNG / WebP · max 5MB. The banner is cropped to fit; keep important artwork centered.
      </p>

      <div className="flex items-center justify-between">
        <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter">PUBLISHING & RIGHTS</h2>
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="edit-profile-btn">
              <User className="w-4 h-4 mr-2" />
              {artistProfile ? 'EDIT PROFILE' : 'SETUP PROFILE'}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-cabinet text-xl font-black uppercase">ARTIST PROFILE & PRO INFO</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleProfileSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Stage Name</Label>
                  <Input value={profileFormData.stage_name}
                    onChange={(e) => setProfileFormData(prev => ({ ...prev, stage_name: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none"
                    required data-testid="profile-stage-name-input" />
                </div>
                <div>
                  <Label className="overline">Legal Name</Label>
                  <Input value={profileFormData.legal_name}
                    onChange={(e) => setProfileFormData(prev => ({ ...prev, legal_name: e.target.value }))}
                    className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none"
                    data-testid="profile-legal-name-input" />
                </div>
              </div>

              <div className="border-t border-[#262626] pt-4">
                <Label className="overline mb-3 block text-[#FF3B30]">Performance Rights Organization (PRO)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-[#A3A3A3]">PRO</Label>
                    <select value={profileFormData.pro}
                      onChange={(e) => setProfileFormData(prev => ({ ...prev, pro: e.target.value }))}
                      className="mt-1 w-full bg-[#0A0A0A] border border-[#262626] p-2 text-white"
                      data-testid="profile-pro-select">
                      <option value="">Select PRO...</option>
                      <option value="BMI">BMI</option>
                      <option value="ASCAP">ASCAP</option>
                      <option value="SESAC">SESAC</option>
                      <option value="GMR">GMR</option>
                      <option value="PRS">PRS (UK)</option>
                      <option value="SOCAN">SOCAN (Canada)</option>
                      <option value="GEMA">GEMA (Germany)</option>
                      <option value="SACEM">SACEM (France)</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3]">IPI/CAE Number</Label>
                    <Input value={profileFormData.ipi_number}
                      onChange={(e) => setProfileFormData(prev => ({ ...prev, ipi_number: e.target.value }))}
                      className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                      placeholder="00000000000" data-testid="profile-ipi-input" />
                  </div>
                </div>
              </div>

              <div className="border-t border-[#262626] pt-4">
                <Label className="overline mb-3 block text-[#FF3B30]">Publisher Information</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-[#A3A3A3]">Publisher Name</Label>
                    <Input value={profileFormData.publisher_name}
                      onChange={(e) => setProfileFormData(prev => ({ ...prev, publisher_name: e.target.value }))}
                      className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                      data-testid="profile-publisher-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3]">Publisher IPI</Label>
                    <Input value={profileFormData.publisher_ipi}
                      onChange={(e) => setProfileFormData(prev => ({ ...prev, publisher_ipi: e.target.value }))}
                      className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                      placeholder="00000000000" data-testid="profile-publisher-ipi-input" />
                  </div>
                </div>
              </div>

              <div>
                <Label className="overline">ISNI (Optional)</Label>
                <Input value={profileFormData.isni}
                  onChange={(e) => setProfileFormData(prev => ({ ...prev, isni: e.target.value }))}
                  className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none"
                  placeholder="0000 0000 0000 0000" data-testid="profile-isni-input" />
              </div>

              <Button type="submit" className="w-full bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="profile-submit-btn">
                SAVE PROFILE
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Artist Profile Card */}
      {artistProfile ? (
        <div className="bg-[#141414] border border-[#262626] p-6">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 bg-[#262626] flex items-center justify-center">
              <User className="w-12 h-12 text-[#A3A3A3]" />
            </div>
            <div className="flex-1">
              <h3 className="font-cabinet text-2xl font-black">{artistProfile.stage_name}</h3>
              {artistProfile.legal_name && <p className="text-[#A3A3A3]">{artistProfile.legal_name}</p>}
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-[#0A0A0A] p-3 border border-[#262626]">
                  <p className="overline text-[#FF3B30] mb-1">PRO</p>
                  <p className="font-bold">{artistProfile.pro || 'Not set'}</p>
                </div>
                <div className="bg-[#0A0A0A] p-3 border border-[#262626]">
                  <p className="overline text-[#FF3B30] mb-1">IPI Number</p>
                  <p className="font-mono">{artistProfile.ipi_number || 'Not set'}</p>
                </div>
                <div className="bg-[#0A0A0A] p-3 border border-[#262626]">
                  <p className="overline text-[#FF3B30] mb-1">Publisher</p>
                  <p className="font-bold">{artistProfile.publisher_name || 'Not set'}</p>
                </div>
                <div className="bg-[#0A0A0A] p-3 border border-[#262626]">
                  <p className="overline text-[#FF3B30] mb-1">Publisher IPI</p>
                  <p className="font-mono">{artistProfile.publisher_ipi || 'Not set'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-[#141414] border border-[#262626] p-12 text-center">
          <User className="w-12 h-12 mx-auto text-[#A3A3A3] mb-4" />
          <p className="text-[#A3A3A3]">No artist profile set up yet.</p>
          <p className="text-[#A3A3A3] text-sm mt-2">Add your BMI/ASCAP IPI number and publisher info.</p>
        </div>
      )}

      {/* Advanced Analytics */}
      {advancedAnalytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <div className="bg-[#141414] border border-[#262626] p-6">
            <h3 className="font-cabinet text-lg font-bold uppercase mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#FF3B30]" /> Geographic
            </h3>
            {advancedAnalytics.geographic?.length > 0 ? (
              <div className="space-y-2">
                {advancedAnalytics.geographic.map((g, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span>{g.country || 'Unknown'}</span>
                    <span className="font-mono text-[#FF3B30]">{g.count}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-[#A3A3A3]">No geographic data yet</p>}
          </div>
          <div className="bg-[#141414] border border-[#262626] p-6">
            <h3 className="font-cabinet text-lg font-bold uppercase mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-[#FF3B30]" /> Devices
            </h3>
            {advancedAnalytics.devices?.length > 0 ? (
              <div className="space-y-2">
                {advancedAnalytics.devices.map((d, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span>{d.device || 'Unknown'}</span>
                    <span className="font-mono text-[#FF3B30]">{d.count}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-[#A3A3A3]">No device data yet</p>}
          </div>
        </div>
      )}
    </div>
  );
}
